import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  DragOverlay,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Column, Card, Client, Project, User } from '../types';
import { getColumns, getCards, getClients, getProjects, moveCard, getUsers, exportCards } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import KanbanColumn from '../components/KanbanColumn';
import TaskCard from '../components/TaskCard';
import CardModal from '../components/CardModal';
import AddCardModal from '../components/AddCardModal';

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = (user?.role || 'admin') === 'admin';
  const [columns, setColumns] = useState<Column[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  const [activeCard, setActiveCard] = useState<Card | null>(null);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [addCardColumnId, setAddCardColumnId] = useState<string | null>(null);

  // Filters
  const [filterClient, setFilterClient] = useState<string>('');
  const [filterProject, setFilterProject] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [myTasksOnly, setMyTasksOnly] = useState<boolean>(false);

  // Can the current user drag this card?
  const canDragCard = useCallback((card: Card): boolean => {
    if (isAdmin) return true;
    return !!user?.id && card.assigneeId === user.id;
  }, [isAdmin, user?.id]);

  // Hidden columns (persisted in localStorage)
  const [hiddenColIds, setHiddenColIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('nwc_hidden_columns');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });

  // Export
  const [showExport, setShowExport] = useState(false);
  const [exportColId, setExportColId] = useState('');
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState('');

  const handleExport = async () => {
    if (!exportColId) {
      setExportError('Please select a stage to export.');
      return;
    }
    if (!exportStart || !exportEnd) {
      setExportError('Please select both a start and end date.');
      return;
    }
    setExportError('');
    setExportLoading(true);
    try {
      const blob = await exportCards(exportColId, exportStart, exportEnd);
      const colName = columns.find((c) => c.id === exportColId)?.name || 'tasks';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${colName.toLowerCase().replace(/\s+/g, '-')}-tasks-${exportStart}-to-${exportEnd}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setShowExport(false);
    } catch (e: any) {
      setExportError(e?.response?.data?.error || 'Export failed. Try again.');
    } finally {
      setExportLoading(false);
    }
  };

  // Mobile
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [mobileColIndex, setMobileColIndex] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const loadData = useCallback(async () => {
    try {
      const [cols, allCards, allClients, allProjects, allUsers] = await Promise.all([
        getColumns(),
        getCards(),
        getClients(),
        getProjects(),
        getUsers(),
      ]);
      setColumns(cols);
      setCards(allCards);
      setClients(allClients);
      setProjects(allProjects);
      setUsers(allUsers);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filtered projects based on selected client
  const filteredProjects = filterClient
    ? projects.filter((p) => p.clientId === filterClient)
    : projects;

  // Apply filters to cards
  const getFilteredCards = (columnId: string) => {
    return cards
      .filter((c) => {
        if (c.columnId !== columnId) return false;
        if (myTasksOnly && (!user?.id || c.assigneeId !== user.id)) return false;
        if (filterClient && c.project?.clientId !== filterClient) return false;
        if (filterProject && c.projectId !== filterProject) return false;
        if (filterPriority && c.priority !== filterPriority) return false;
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          if (!c.title.toLowerCase().includes(q) && !(c.description || '').toLowerCase().includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => a.position - b.position);
  };

  // DnD handlers
  const handleDragStart = (event: DragStartEvent) => {
    const card = cards.find((c) => c.id === event.active.id);
    if (!card || !canDragCard(card)) return;
    setActiveCard(card);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeCard = cards.find((c) => c.id === activeId);
    if (!activeCard || !canDragCard(activeCard)) return;

    // Check if over a column
    const overColumn = columns.find((col) => col.id === overId);
    if (overColumn && activeCard.columnId !== overId) {
      setCards((prev) =>
        prev.map((c) =>
          c.id === activeId ? { ...c, columnId: overId } : c
        )
      );
      return;
    }

    // Over another card
    const overCard = cards.find((c) => c.id === overId);
    if (overCard && activeCard.columnId !== overCard.columnId) {
      setCards((prev) =>
        prev.map((c) =>
          c.id === activeId ? { ...c, columnId: overCard.columnId } : c
        )
      );
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveCard(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const card = cards.find((c) => c.id === activeId);
    if (!card) return;
    if (!canDragCard(card)) return;

    // Determine target column
    let targetColumnId = card.columnId;
    const overColumn = columns.find((col) => col.id === overId);
    if (overColumn) {
      targetColumnId = overColumn.id;
    } else {
      const overCard = cards.find((c) => c.id === overId);
      if (overCard) targetColumnId = overCard.columnId;
    }

    // Build new ordered list for target column (without active card)
    const columnCards = cards
      .filter((c) => c.columnId === targetColumnId && c.id !== activeId)
      .sort((a, b) => a.position - b.position);

    // Find insertion index
    const overCard = cards.find((c) => c.id === overId);
    let insertIndex = columnCards.length;
    if (overCard && overCard.columnId === targetColumnId) {
      const idx = columnCards.findIndex((c) => c.id === overId);
      if (idx !== -1) insertIndex = idx;
    }

    const newPosition = insertIndex;

    // Build fully resequenced list for target column (avoids duplicate positions)
    const resequenced = [
      ...columnCards.slice(0, insertIndex),
      { ...card, columnId: targetColumnId },
      ...columnCards.slice(insertIndex),
    ].map((c, i) => ({ ...c, position: i }));

    // Optimistic update: replace all target-column cards with resequenced versions
    setCards((prev) => {
      const rest = prev.filter((c) => c.columnId !== targetColumnId && c.id !== activeId);
      return [...rest, ...resequenced];
    });

    // Persist to API
    try {
      const updatedCard = await moveCard(activeId, targetColumnId, newPosition);
      setCards((prev) =>
        prev.map((c) => (c.id === activeId ? updatedCard : c))
      );
    } catch (e) {
      console.error('Failed to move card:', e);
      loadData();
    }
  };

  const handleCardUpdate = (updated: Card) => {
    setCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setSelectedCard(updated);
  };

  const handleCardDelete = (cardId: string) => {
    setCards((prev) => prev.filter((c) => c.id !== cardId));
    setSelectedCard(null);
  };

  const handleCardCreated = (card: Card) => {
    setCards((prev) => [...prev, card]);
    setAddCardColumnId(null);
  };

  const activeFilters = [filterClient, filterProject, filterPriority, searchQuery, myTasksOnly ? 'me' : ''].filter(Boolean).length;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <div style={{ color: '#64748B', fontSize: 14 }}>Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Board header */}
      <div
        style={{
          padding: isMobile ? '10px 14px 8px' : '20px 24px 16px',
          borderBottom: '1px solid #E2E8F0',
          background: 'white',
          flexShrink: 0,
        }}
      >
        {isMobile ? (
          // Mobile: search + client filter in one compact row
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <svg
                width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2"
                style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
              >
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search cards..."
                style={{ paddingLeft: 28, width: '100%' }}
              />
            </div>
            {isAdmin ? (
              <select
                value={filterClient}
                onChange={(e) => { setFilterClient(e.target.value); setFilterProject(''); }}
                style={{ width: 120, flexShrink: 0 }}
              >
                <option value="">All Clients</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            ) : (
              <select
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
                style={{ width: 140, flexShrink: 0 }}
              >
                <option value="">All Projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}
            {activeFilters > 0 && (
              <button
                onClick={() => { setFilterClient(''); setFilterProject(''); setFilterPriority(''); setSearchQuery(''); setMyTasksOnly(false); }}
                className="btn btn-ghost btn-sm"
                style={{ color: '#DC2626', flexShrink: 0, padding: '6px 8px' }}
                title="Clear filters"
              >
                ×
              </button>
            )}
          </div>
        ) : (
          // Desktop: title + full filter row
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <h1 style={{ fontSize: 20, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.3px' }}>
                Task Board
              </h1>
              <div style={{ fontSize: 13, color: '#64748B', marginTop: 2 }}>
                {cards.length} cards across {columns.length} stages
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ position: 'relative' }}>
                <svg
                  width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2"
                  style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                >
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search cards..."
                  style={{ paddingLeft: 32, width: 180 }}
                />
              </div>
              {isAdmin ? (
                <>
                  <select value={filterClient} onChange={(e) => { setFilterClient(e.target.value); setFilterProject(''); }} style={{ width: 140 }}>
                    <option value="">All Clients</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  {filterClient && (
                    <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} style={{ width: 160 }}>
                      <option value="">All Projects</option>
                      {filteredProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  )}
                </>
              ) : (
                <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} style={{ width: 180 }}>
                  <option value="">All Projects</option>
                  {projects.map((p) => {
                    const clientName = clients.find((c) => c.id === p.clientId)?.name;
                    return (
                      <option key={p.id} value={p.id}>
                        {clientName ? `${clientName} · ${p.name}` : p.name}
                      </option>
                    );
                  })}
                </select>
              )}
              <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} style={{ width: 130 }}>
                <option value="">All Priorities</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <label
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px',
                  borderRadius: 100,
                  border: `1px solid ${myTasksOnly ? '#2563EB' : '#E2E8F0'}`,
                  background: myTasksOnly ? '#EFF6FF' : 'white',
                  color: myTasksOnly ? '#1D4ED8' : '#64748B',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  margin: 0, whiteSpace: 'nowrap',
                }}
              >
                <input
                  type="checkbox"
                  checked={myTasksOnly}
                  onChange={(e) => setMyTasksOnly(e.target.checked)}
                  style={{ width: 'auto', margin: 0 }}
                />
                My tasks only
              </label>
              {activeFilters > 0 && (
                <button onClick={() => { setFilterClient(''); setFilterProject(''); setFilterPriority(''); setSearchQuery(''); setMyTasksOnly(false); }} className="btn btn-ghost btn-sm" style={{ color: '#DC2626' }}>
                  × Clear ({activeFilters})
                </button>
              )}
              <button
                onClick={() => {
                  const defaultCol = columns.find((c) => c.name.toLowerCase().includes('complete'));
                  setExportColId(defaultCol?.id || columns[columns.length - 1]?.id || '');
                  setExportStart('');
                  setExportEnd('');
                  setExportError('');
                  setShowExport(true);
                }}
                className="btn btn-ghost btn-sm"
                style={{ border: '1px solid #E2E8F0', color: '#475569', display: isAdmin ? undefined : 'none' }}
                title="Export completed tasks"
              >
                ↓ Export
              </button>
              {isAdmin && (
                <button onClick={() => setAddCardColumnId(columns[0]?.id || '')} className="btn btn-primary btn-sm">
                  + Add Card
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Board */}
      {isMobile ? (
        // ── Mobile: column tabs + single-column card list ──
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Column tabs */}
          <div style={{
            display: 'flex',
            overflowX: 'auto',
            padding: '0 16px',
            background: 'white',
            borderBottom: '1px solid #E2E8F0',
            gap: 2,
            flexShrink: 0,
          }}>
            {columns.filter((c) => !hiddenColIds.has(c.id)).map((col, i) => {
              const count = getFilteredCards(col.id).length;
              const isActive = i === mobileColIndex;
              return (
                <button
                  key={col.id}
                  onClick={() => setMobileColIndex(i)}
                  style={{
                    padding: '10px 14px',
                    border: 'none',
                    background: 'transparent',
                    color: isActive ? '#2563EB' : '#64748B',
                    fontWeight: isActive ? 700 : 500,
                    fontSize: 13,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    borderBottom: isActive ? '2px solid #2563EB' : '2px solid transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    flexShrink: 0,
                    fontFamily: 'inherit',
                  }}
                >
                  {col.name}
                  <span style={{
                    background: isActive ? '#2563EB' : '#E2E8F0',
                    color: isActive ? 'white' : '#64748B',
                    borderRadius: 100,
                    padding: '1px 7px',
                    fontSize: 11,
                    fontWeight: 700,
                  }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Cards for selected column */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px 80px' }}>
            {columns[mobileColIndex] && (() => {
              const colCards = getFilteredCards(columns[mobileColIndex].id);
              return (
                <>
                  {colCards.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#94A3B8', padding: '48px 0', fontSize: 14 }}>
                      No cards here
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {colCards.map((card) => (
                      <TaskCard key={card.id} card={card} onClick={() => setSelectedCard(card)} />
                    ))}
                  </div>
                  <button
                    onClick={() => setAddCardColumnId(columns[mobileColIndex].id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      width: '100%',
                      marginTop: 12,
                      padding: '13px',
                      border: '1.5px dashed #CBD5E1',
                      borderRadius: 12,
                      background: 'transparent',
                      color: '#64748B',
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    + Add card to {columns[mobileColIndex].name}
                  </button>
                </>
              );
            })()}
          </div>

          {/* Mobile FAB */}
          <button
            onClick={() => setAddCardColumnId(columns[mobileColIndex]?.id || columns[0]?.id || '')}
            style={{
              position: 'fixed',
              bottom: 24,
              right: 20,
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: '#2563EB',
              color: 'white',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(37,99,235,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
              fontFamily: 'inherit',
            }}
            title="Add card"
          >
            +
          </button>
        </div>
      ) : (
        // ── Desktop: horizontal kanban with drag-and-drop ──
        <div
          style={{
            flex: 1,
            overflowX: 'auto',
            overflowY: 'hidden',
            padding: '20px 24px',
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div
              style={{
                display: 'flex',
                gap: 16,
                height: '100%',
                minWidth: 'max-content',
              }}
            >
              {columns.filter((c) => !hiddenColIds.has(c.id)).map((column) => (
                <KanbanColumn
                  key={column.id}
                  column={column}
                  cards={getFilteredCards(column.id)}
                  onCardClick={(card) => setSelectedCard(card)}
                  onAddCard={(colId) => setAddCardColumnId(colId)}
                  isAdmin={isAdmin}
                  canDragCard={canDragCard}
                />
              ))}
            </div>

            <DragOverlay>
              {activeCard ? (
                <div style={{ opacity: 0.9, transform: 'rotate(2deg)' }}>
                  <TaskCard card={activeCard} onClick={() => {}} />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      )}

      {/* Card detail modal */}
      {selectedCard && (
        <CardModal
          card={selectedCard}
          columns={columns}
          projects={projects}
          clients={clients}
          users={users}
          onClose={() => setSelectedCard(null)}
          onUpdate={handleCardUpdate}
          onDelete={handleCardDelete}
          currentUserName={user?.name || 'Admin'}
          isAdmin={isAdmin}
        />
      )}

      {/* Add card modal */}
      {addCardColumnId !== null && (
        <AddCardModal
          columns={columns}
          projects={projects}
          clients={clients}
          users={users}
          defaultColumnId={addCardColumnId}
          onClose={() => setAddCardColumnId(null)}
          onCreated={handleCardCreated}
        />
      )}

      {/* Export Modal */}
      {showExport && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowExport(false); }}
        >
          <div style={{
            background: 'white', borderRadius: 16, padding: 28, width: 380, maxWidth: '90vw',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0F172A', marginBottom: 20 }}>
              Export Tasks
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 8 }}>Stage</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {columns.map((col) => (
                    <label
                      key={col.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 7,
                        padding: '6px 12px', borderRadius: 20, cursor: 'pointer',
                        background: exportColId === col.id ? '#EFF6FF' : '#F8FAFC',
                        border: `1px solid ${exportColId === col.id ? '#BFDBFE' : '#E2E8F0'}`,
                        fontSize: 13, fontWeight: exportColId === col.id ? 600 : 400,
                        color: exportColId === col.id ? '#1D4ED8' : '#475569',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <input
                        type="radio"
                        name="exportCol"
                        value={col.id}
                        checked={exportColId === col.id}
                        onChange={() => setExportColId(col.id)}
                        style={{ accentColor: '#2563EB', margin: 0 }}
                      />
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: col.color, flexShrink: 0 }} />
                      {col.name}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Start Date</label>
                <input
                  type="date"
                  value={exportStart}
                  onChange={(e) => setExportStart(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>End Date</label>
                <input
                  type="date"
                  value={exportEnd}
                  onChange={(e) => setExportEnd(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              {exportError && (
                <div style={{ color: '#DC2626', fontSize: 13 }}>{exportError}</div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                <button
                  onClick={() => setShowExport(false)}
                  className="btn btn-ghost btn-sm"
                  style={{ color: '#64748B' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleExport}
                  disabled={exportLoading}
                  className="btn btn-primary btn-sm"
                >
                  {exportLoading ? 'Exporting...' : '↓ Download CSV'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
